import { Dimensions, TextInput, Platform, StyleSheet, ScrollView} from 'react-native';
import { themeColorSet, ThemedPressable, ThemedText, ThemedView, themeTextVariants } from '@app/theme';
import Button from '@app/sharedComponents/Button/Button';
import CommonHeader from '@app/sharedComponents/CommonHeader/CommonHeader';
import { useRef, useState, useEffect } from 'react';
import { PrivateStackParamList } from '@app/navigationApp/types';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import InputText from '@app/sharedComponents/TextInput/TextInput';
import { formatIndianCurrency, numberToWordsIndian } from '@app/utils/utility';
import ROUTE from '@app/navigationApp/routes';
import axios from 'axios';
import { endpoints } from '@app/api/apis';
import { useAppSelector, useAppDispatch } from '@app/store/hooks';
import { showErrorSheet } from '@app/store/slices/errorSlice';
import handleApiErr from '@app/helper/handleApiErr';
import useCreditDetails from '@app/hooks/useCreditDetails';
import usePendingRequestDetails from '@app/hooks/usePendingRequestDetails';
import useDisbursalDetails from '@app/hooks/useDisbursalDetails';
import SystemError from '../../../assets/Icons/SystemErrorIcon';
import BottomSheetContainer from '@app/sharedComponents/Bottomsheet/Bottomsheet';
import {Info} from '@app/sharedComponents/Icons/Icons';
import AccountOpeningInfo from '../ConfirmWithdrawAmount/components/AccountOpeningInfo';
import { ThemedTouchableOpacity } from '@app/theme/Components';
import PageLoader from '@app/sharedComponents/PageLoader/PageLoader';
const screenWidth = Dimensions.get('window').width;

type ScreenProps = NativeStackScreenProps<PrivateStackParamList, 'WITHDRAW_AMOUNT'>;

const EnterWithdrawalAmount = ({navigation, route} : ScreenProps) => {
    const dispatch = useAppDispatch()
    const creditId = useAppSelector((state) => state.auth.userDetails?.linkedCredits[0]?.creditId);
    
    const { creditInfo, isLoading: creditInfoLoading } = useCreditDetails();
    const { pendingRequestDetails, isLoading: pendingRequestsLoading } = usePendingRequestDetails();
    const { isNotFirstDisbursal, isLoading: disbursalLoading } = useDisbursalDetails();
    
    const [inputAmount, setInputAmount] = useState<string>(String(route?.params?.amount|| ''));
    const inputRef = useRef<TextInput>(null);
    const [buttonLoader, setButtonLoader] = useState(false);
    const [isBottomSheetVisible, setIsBottomSheetVisible] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [showAccountOpeningInfo, setShowAccountOpeningInfo] = useState(false);
    const [accountOpeningInfo, setAccountOpeningInfo] = useState({});
    const {isfromTDL} = useAppSelector(state => state.platformParams);
    
    const closeBottomSheet = () => {
      setIsBottomSheetVisible(false);
      setTimeout(() => {
        setErrorMessage(null);
      }, 1000);
      setShowAccountOpeningInfo(false);

    };
    const onContinue = () => {
      setButtonLoader(true)
        const payload ={
          disbursalAmount:inputAmount,
          creditId
        }
        axios.post(endpoints.checkWithdraw,payload)
        .then((res)=>{
          navigation.navigate(ROUTE.CONFIRM_WITHDRAW_AMOUNT , {withdrawDetails:{...res.data,creditId}});
        }).catch((err)=>{
          console.log('checkWithdraw',err);
         // Check for network error and do nothing
         if(err?.data?.voltErrorCode === 'AMOUNT_LESS_THAN_MINIMUM_AND_FEES'){
          setIsBottomSheetVisible(true)
          setErrorMessage(err?.data);
         }
         else if (handleApiErr(err)) {
            dispatch(
              showErrorSheet({
                isErrorSheetVisible: true,
                errorMessage: err?.data?.message,
                closeOnBackdropPress: true,
                errorType: err?.data?.voltErrorCode || err?.data?.statusCode,
                errorHeading : err?.data?.header,
                btnName: 'Ok, got it!'
              }),
            );
          }
        }).finally(()=>{
          setButtonLoader(false)
        })
    }

    const onInput = (text:string) => {
      let formattedText = text.replace(/[^0-9]/g, '');
      if(formattedText.length>1 && formattedText[0] === '0'){
        formattedText = formattedText.slice(1);
      }
      setInputAmount(formattedText);
    }

    const toggleAccountFees = ()=>{
      axios.post(endpoints.accountOpenInfo,{disbursalAmount:inputAmount, creditId})
      .then((res)=>{
        setAccountOpeningInfo(res?.data);
        setShowAccountOpeningInfo(true);
      }
      ).catch((err)=>{
        console.log('accountOpenInfo',err);
      })
    }
    const amountInWords = numberToWordsIndian(Number(inputAmount));

    if (creditInfoLoading || pendingRequestsLoading || disbursalLoading) {
        return <PageLoader/>;
    }
    
    return(
    <>
        <CommonHeader navigation={navigation} pageName='WITHDRAW_AMOUNT' label="Enter withdrawal amount" />
        <ScrollView 
          contentContainerStyle={{flexGrow: 1, padding:16, backgroundColor:themeColorSet['bg-surface-primary'],paddingBottom: 100}}>
        {
          isfromTDL ?
           <ThemedView mt='sm' mb="2xl" flexDirection='row' justifyContent='space-between' borderRadius='md' backgroundColor='bg-fill-secondary' p="md" borderWidth={1} borderColor='border-primary'>
              <ThemedText variant='b3r' color='text-disabled' mr='sm'>Available cash</ThemedText>
              <ThemedText variant='b3s' color='text-primary' >₹{formatIndianCurrency(Math.floor(creditInfo?.availableCreditAmount))}</ThemedText>
          </ThemedView> :
          <ThemedView mt='sm' mb="2xl" flexDirection='row'>
              <ThemedText variant='b3r' color='text-secondary' mr='sm'>Available cash:</ThemedText>
              <ThemedText variant='b3s' color='text-primary' >₹{formatIndianCurrency(Math.floor(creditInfo?.availableCreditAmount))}</ThemedText>
          </ThemedView>
          }
          <InputText
              height={65}
              placeHolder={'0'}
              ref={inputRef}
              overRideInputStyles={themeTextVariants.l1}
              overRideContainerStyles={{}}
              keyboardType="phone-pad"
              onChangeText={onInput}
              value={inputAmount}
              showLabel
              label={'Enter amount'}
              showLeftAccessory
              leftAccessory={() => {
                return (
                    <ThemedText variant='l1' color='text-primary' mr="sm" style={{marginTop:Platform.OS === 'ios' ? 5 : 0}}>
                      ₹
                    </ThemedText>
                );
              }}
          />
          {Number(inputAmount) >0 && !amountInWords?.includes('undefined') &&
          <ThemedText mt="lg" variant="b4r" color='text-secondary'>
            Rupees {amountInWords}
          </ThemedText>}
          {pendingRequestDetails?.revocationRequestDetailsList?.length>0 &&
          <ThemedView p="md" mt="lg" mb="lg" backgroundColor='bg-fill-warning' borderRadius='sm' flexDirection='row' alignItems='center'>
            <Info weight="fill" color={themeColorSet["icon-warning"]} size={20}/>
            <ThemedView  ml="sm" flexShrink={1}>
              <ThemedText variant='b4b' color='text-primary' mb="xs">Un-pledge request in progress</ThemedText>
              <ThemedText variant='b4r' color="text-secondary">Withdrawals may affect the approval of your ongoing un-pledge request.</ThemedText>
            </ThemedView>
          </ThemedView>}
        </ScrollView>
        <ThemedView backgroundColor='bg-surface-primary' style={[Platform.OS === 'web' ? ls.webBtn:ls.nativeBtn ]}>
        <ThemedView height={1} width={screenWidth} backgroundColor='border-primary' />
        <ThemedView m="lg">
          <Button
            eventName={route?.name + '_CONTINUE'}
            label={'Proceed to confirm amount'}
            handlePress={onContinue}
            type={'SOLID'}
            size='MD'
            disabled={Number(inputAmount)<=0}
            buttonLoader={buttonLoader}
          />
        </ThemedView>
      </ThemedView>
      <BottomSheetContainer
      isVisible={isBottomSheetVisible}
      close={closeBottomSheet}
      closeOnBackdropPress={true}
      borderTopRightRadius={10}
      borderTopLeftRadius={10}
      containerStyle={{backgroundColor:'#fefefe'}}>
        {showAccountOpeningInfo ?
        <AccountOpeningInfo 
            accountOpeningFee={accountOpeningInfo?.accountOpeningFee}
            closeBottomSheet={closeBottomSheet}
            processingFee={accountOpeningInfo?.processingFee}
            gstOnProcessingFee={accountOpeningInfo?.gstOnProcessingFee}
            stampDutyFee={accountOpeningInfo?.stampDutyFee}
            lienMarkingFee={accountOpeningInfo?.lienMarkingFee}
        />:
        <ThemedView p="lg" justifyContent="center" alignItems="center">
          <ThemedView mt="xs" mb="2xl">
            <SystemError size={36} />
          </ThemedView>
          <ThemedText color='text-primary' variant='h5s' mb="sm">{errorMessage?.header || 'Verfication failed!'}</ThemedText>
          <ThemedView  backgroundColor='bg-fill-secondary' py="sm" px="lg" mb="2xl" alignItems='center' justifyContent='center'  borderRadius='sm' flexDirection='row'>
            <ThemedText variant='b3r' color="text-secondary"textAlign='center'>
            {errorMessage?.message}
            {!isNotFirstDisbursal&&
            <ThemedTouchableOpacity onPress={toggleAccountFees}>
              <ThemedView style={{position:'relative',top:Platform.OS === 'android' ? 3 : 0,left:5}}>
                <Info size={16}/>
              </ThemedView>
            </ThemedTouchableOpacity>
            }
            </ThemedText>
           
          </ThemedView>
          <Button
            eventName={route?.name + '_EDIT'}
            label={'Ok, got it'}
            type={'SOLID'}
            handlePress={closeBottomSheet}
            size='MD'
            />
        </ThemedView>
        }
      </BottomSheetContainer>
    </> 

    )
}

export default EnterWithdrawalAmount;
const ls = StyleSheet.create({
  nativeBtn:{
    position:'absolute',
    bottom:0,
  },
  webBtn:{
    position: screenWidth>450 ?'absolute':'fixed',
    bottom:0,
    left:0,
    width:'100%'
  }
});